import { useRef, useEffect, useState } from 'react';
import MainFormContainer from '@/wrappers/MainFormContainer';
import PostcodeElement, { type PostcodeElementRef } from '@/elements/Postcode';
import { useRouter } from '@tanstack/react-router';
import { updateBasicInfo } from '@/utils/client-storage/localStorage';
import { GET_JOURNEY_PAGES } from '@/routes/journey.routes/index';
// import imgSignature from '@/assets/img/Signature.svg';
import { LcpDisclaimer } from '@/components/LcpDisclaimer';
import TrustpilotCarousel from '@/components/TrustpilotCarousel';
import clsx from 'clsx';
import { ArrowLeft, Clock, Info, ShieldCheck } from 'lucide-react';
import { clearAllSessionData, triggerSub2DataLayer } from '@/utils/functions';
import { SUB2_EVENTS } from '@/services/constants';

export default function Postcode({
  homepageMode = false,
  hideAfterForm = false,
}: {
  homepageMode?: boolean;
  hideAfterForm?: boolean;
}) {
  const postcodeRef = useRef<PostcodeElementRef | null>(null);
  const router = useRouter();
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const utmSource = urlParams.get('utm_source');
  const isDM = utmSource && utmSource.includes('DM');

  const isMailDomain =
    typeof window !== 'undefined' && (window.location.hostname === 'localhost' || isDM);

  const [socialAnswer, setSocialAnswer] = useState<'Yes' | 'No' | null>(null);
  const [hasProceeded, setHasProceeded] = useState(false);

  const isSocialDomain =
    typeof window !== 'undefined' && window.location.hostname.includes('social');

  useEffect(() => {
    clearAllSessionData();
  }, []);

  const handleSubmit = async () => {
    const pcData = postcodeRef.current?.getData();
    const { postcode, selectedPostcode, addresses } = pcData ?? {
      postcode: '',
      selectedPostcode: '',
      addresses: [],
    };

    const finalPostcode = selectedPostcode || postcode;

    if (!finalPostcode || !postcodeRef.current?.validate()) {
      return;
    }

    updateBasicInfo({
      postcode: finalPostcode,
      financedMoreThanOneVehicleSince2007: socialAnswer,
    });

    const destination = 'ADDRESS';
    const pages = GET_JOURNEY_PAGES() as any;

    if (pages && pages[destination]) {
      router.navigate({
        to: pages[destination].path,
        state: {
          prefetchedAddresses: addresses,
          postcode: finalPostcode,
        } as any,
      });
    } else {
      console.error('Address lookup page not found');
    }
  };

  const postCodeElementComp = (
    <form
      id="postcode-section"
      onSubmit={(e) => e.preventDefault()}
      className={clsx('w-full', !homepageMode && 'px-5')}
    >
      <PostcodeElement
        ref={postcodeRef}
        homepageMode={homepageMode}
        triggerQueryParams={true}
        title="Please enter your Current Address"
        onSubmit={handleSubmit}
      />
    </form>
  );

  const compIternal = (
    <>
      {homepageMode &&
        (isMailDomain || isSocialDomain) &&
        localStorage.getItem('is_partner_mode') !== 'true' && (
          <div className="inline-flex items-center gap-2 bg-white border border-primary/30 rounded-full px-4 py-2 self-start">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-primary font-semibold text-body lg:text-body">
              {isSocialDomain ? (
                'Welcome — let’s get started.'
              ) : (
                <>Thanks for scanning &mdash; welcome.</>
              )}
            </span>
          </div>
        )}

      {homepageMode && isSocialDomain && !hasProceeded && (
        <div className="w-full flex flex-col items-start gap-4 mb-2">
          {socialAnswer === 'No' ? (
            <>
              <h2 className="text-title lg:text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
                You are not <span className="text-primary">eligible</span>.
              </h2>
              <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
                You need to have financed more than one vehicle since 2007.
              </div>
              <div className="flex items-start gap-3 bg-white border border-primary/10 rounded-xl p-4 mt-2 mb-1">
                <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <p className="text-body text-text-main font-medium leading-tight">
                  If you think you might have had car finance since 2007, you can click go back and
                  update your selection.
                </p>
              </div>
              <div className="w-full mt-2">
                <button
                  type="button"
                  onClick={() => setSocialAnswer(null)}
                  className="relative w-full py-4 px-6 border-2 rounded-lg md:rounded-2xl text-center transition-all flex items-center justify-center min-h-[64px] md:min-h-[82px] border-primary bg-primary text-white hover:bg-[#14532d] shadow-sm pulsating-btn gap-2"
                >
                  <ArrowLeft className="w-6 h-6" strokeWidth={3} />
                  <span className="font-extrabold text-title">Go Back</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-title lg:text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
                Have you financed more than one vehicle since{' '}
                <span className="text-primary">2007</span>?
              </h2>
              <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
                We find on average 2 agreements per client, worth{' '}
                <span className="text-primary font-bold text-subtitle">&pound;1,658*</span>
              </div>
              <div className="w-full flex flex-col gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSocialAnswer('Yes');
                    triggerSub2DataLayer(SUB2_EVENTS.car_financed_0721);
                    setHasProceeded(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="relative w-full py-4 px-6 border-2 rounded-lg md:rounded-2xl text-center transition-all flex items-center justify-center min-h-[64px] md:min-h-[82px] border-primary bg-primary text-white hover:bg-[#14532d] shadow-sm"
                >
                  <span className="font-extrabold text-title">Yes</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSocialAnswer('No')}
                  className="relative w-full py-4 px-6 border-2 rounded-lg md:rounded-2xl text-center transition-all flex items-center justify-center min-h-[64px] md:min-h-[82px] border-footer bg-footer text-white hover:opacity-90 active:scale-[0.98] shadow-sm"
                >
                  <span className="font-extrabold text-title">No</span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Header Group */}
      {(!homepageMode || !isSocialDomain || hasProceeded) && (
        <div className="w-full flex flex-col items-start gap-4">
          <h1 className="text-hero font-black text-text-main leading-[1.05] tracking-tight text-left w-full">
            Let&rsquo;s see if you <span className="text-primary">could be owed</span> a refund.
          </h1>
          <div className="text-text-main text-subtitle lg:text-body font-medium leading-snug -mt-1 w-full text-left">
            Pop your postcode to see if you could be owed{' '}
            <span className="text-primary font-bold text-subtitle">&pound;1,658*</span>
          </div>
        </div>
      )}

      {/* Input & Trust Group - tight gap */}
      <div className="w-full flex flex-col items-start gap-4">
        {(!homepageMode || !isSocialDomain || hasProceeded) && postCodeElementComp}

        {/* Trust & Footer Group */}
        <div className="w-full flex flex-col items-start gap-4">
          <div className="flex items-center justify-center gap-x-2 lg:gap-x-5 text-[clamp(10px,3.2vw,16px)] font-bold text-text-main w-full whitespace-nowrap">
            <div className="flex items-center gap-1">
              <span>🔒</span>
              <span>Secure</span>
            </div>
            <span className="text-gray-300 font-light">&bull;</span>
            <div className="flex items-center gap-1.5">
              <Clock size={15} className="text-primary shrink-0" strokeWidth={2.5} />
              <span>Takes 60 seconds</span>
            </div>
            <span className="text-gray-300 font-light">&bull;</span>
            <div className="flex items-center gap-1.5">
              <ShieldCheck size={16} className="text-primary shrink-0" strokeWidth={2.5} />
              <span>No credit hit</span>
            </div>
          </div>

          {homepageMode && (
            <div className="w-full">
              <TrustpilotCarousel />
            </div>
          )}

          {homepageMode && (
            <div className="w-full flex flex-col items-start gap-4 pt-6 border-t border-dashed border-primary/30">
              <div className="flex items-center gap-3.5">
                {/* <img
                  src={imgSignature}
                  alt="Signature"
                  width={40}
                  height={40}
                  className="w-10 h-10 flex-shrink-0"
                /> */}
                <div className="flex flex-col leading-tight">
                  <span className="text-text-main font-bold text-body tracking-tight">
                    Scott Mandeville
                  </span>
                  <span className="text-text-muted text-body font-medium mt-1">
                    Customer Care Specialist
                  </span>
                  {/* <span className="text-primary font-bold text-micro mt-0.5 uppercase tracking-wide">
                    FCA FRN 830181
                  </span> */}
                </div>
              </div>

              <LcpDisclaimer />
            </div>
          )}

          {!homepageMode && (
            <div className="text-page-desc px-5">
              Check your eligibility today — simple, and clear
            </div>
          )}
        </div>
      </div>
    </>
  );

  const getProgress = () => {
    if (homepageMode) return 0;
    return 20;
  };

  return (
    <MainFormContainer
      progress={getProgress()}
      hideAfterForm={hideAfterForm}
      currentStep={homepageMode ? undefined : 1}
      totalSteps={homepageMode ? undefined : 5}
      isHome={homepageMode}
      contentClassName={`p-5 lg:pb-20 flex flex-col items-start gap-6 ${
        homepageMode ? 'lg:px-0' : 'lg:px-20'
      }`}
    >
      {compIternal}
    </MainFormContainer>
  );
}
